import { FlaskConical, Clock, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * The one screen an account that is not yet approved may see.
 *
 * Rendered IN PLACE by ProtectedRoute rather than redirected to, deliberately:
 * there is no /application-received address to bookmark, share or bypass, and
 * every protected address shows this instead of its page. A redirect would also
 * have to render something first, which is the dashboard flash this exists to
 * prevent.
 *
 * The signed-in email is shown because the commonest confusion here is signing
 * in with the wrong account — a personal address instead of an institutional
 * one — and staring at a screen that says "awaiting approval" for an
 * application that was never made under that address. Sign-out is the only
 * action, and it is the right one for that case.
 *
 * A rejected account currently sees this same screen. That is a deliberate
 * interim: telling someone they were rejected is a message worth writing
 * carefully, and it is not written yet. The decision that routes here already
 * distinguishes the two, so a separate screen is a rendering change only.
 */
export default function ApplicationReceived() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <FlaskConical className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">DocumentSpark</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-elevated p-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full mb-5">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>

          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Application received
          </h2>

          <p className="text-gray-600 mb-6">
            Your application is with our administrators for review. You will be
            able to sign in and start work as soon as it is approved.
          </p>

          <div className="bg-gray-50 rounded-xl px-4 py-3 mb-6">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
              Signed in as
            </p>
            <p className="text-sm font-medium text-gray-900 break-all">
              {user?.email ?? 'Unknown account'}
            </p>
          </div>

          <p className="text-sm text-gray-500 mb-6">
            If this is not the account you applied with, sign out and sign in
            with the right one.
          </p>

          <button
            onClick={() => {
              void logout();
            }}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
